import { useState, useEffect, useRef } from 'react';
import API from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Users, Plus, Pencil, Trash2, Phone, Mail, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { CALL_STATUSES, fmtPhone } from '@/lib/buyersV2';

const EMPTY = { first_name: '', last_name: '', title: '', phones: [{ number: '', type: 'mobile', dnc: false }], emails: [''], litigator: false, sms_opt_in: false, sms_opt_in_source: '', email_opt_out: false, call_status: 'Not Called' };
const cls = 'bg-white border-gray-300 h-9 text-sm';

function PhoneRow({ p, onChange, onRemove, i }) {
  return (
    <div className="flex items-center gap-2">
      <Input data-testid={`contact-phone-${i}`} value={p.number} onChange={e => onChange({ ...p, number: e.target.value })} placeholder="Phone" className={`${cls} flex-1`} />
      <Select value={p.type} onValueChange={v => onChange({ ...p, type: v })}>
        <SelectTrigger className={`${cls} w-28`}><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white"><SelectItem value="mobile">Mobile</SelectItem><SelectItem value="landline">Landline</SelectItem></SelectContent>
      </Select>
      <label className="flex items-center gap-1 text-xs text-gray-600"><Switch checked={p.dnc} onCheckedChange={v => onChange({ ...p, dnc: v })} />DNC</label>
      <button onClick={onRemove} className="text-gray-300 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  );
}

function ContactDialog({ open, onOpenChange, contact, buyerId, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const keySeq = useRef(0);
  const withKey = v => ({ v, k: ++keySeq.current });
  useEffect(() => { if (open) setF(contact ? { ...EMPTY, ...contact, emails: contact.emails?.length ? contact.emails : [''], phones: contact.phones?.length ? contact.phones.map(p => ({ ...p })) : [] } : EMPTY); }, [open, contact]);
  const phoneKeys = useRef([]); const emailKeys = useRef([]);
  const syncKeys = (ref, n) => { while (ref.current.length < n) ref.current.push(withKey().k); ref.current.length = n; return ref.current; };
  const removeKey = (ref, i) => { ref.current = ref.current.filter((_, j) => j !== i); };
  const up = (k, v) => setF(p => ({ ...p, [k]: v }));
  const save = async () => {
    const body = { ...f, emails: f.emails.filter(Boolean), phones: f.phones.filter(p => p.number) };
    try {
      if (contact) await API.put(`/buyers/${buyerId}/contacts/${contact.id}`, body);
      else await API.post(`/buyers/${buyerId}/contacts`, body);
      toast.success('Contact saved'); onOpenChange(false); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save contact'); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="contact-dialog">
        <DialogHeader><DialogTitle>{contact ? 'Edit Contact' : 'Add Contact'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-2">
          <Input data-testid="contact-first" value={f.first_name} onChange={e => up('first_name', e.target.value)} placeholder="First name" className={cls} />
          <Input data-testid="contact-last" value={f.last_name} onChange={e => up('last_name', e.target.value)} placeholder="Last name" className={cls} />
          <Input value={f.title} onChange={e => up('title', e.target.value)} placeholder="Title (MGR, AMBR)" className={cls} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-gray-500">Phones (up to 3)</Label>
          {f.phones.map((p, i) => <PhoneRow key={syncKeys(phoneKeys, f.phones.length)[i]} i={i} p={p} onChange={np => up('phones', f.phones.map((x, j) => j === i ? np : x))} onRemove={() => { removeKey(phoneKeys, i); up('phones', f.phones.filter((_, j) => j !== i)); }} />)}
          {f.phones.length < 3 && <button data-testid="contact-add-phone" onClick={() => up('phones', [...f.phones, { number: '', type: 'mobile', dnc: false }])} className="text-xs text-[#0073ea]">+ Add phone</button>}
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-gray-500">Emails (up to 2)</Label>
          {f.emails.map((e, i) => <Input key={syncKeys(emailKeys, f.emails.length)[i]} data-testid={`contact-email-${i}`} value={e} onChange={ev => up('emails', f.emails.map((x, j) => j === i ? ev.target.value : x))} placeholder="email@company.com" className={cls} />)}
          {f.emails.length < 2 && <button onClick={() => up('emails', [...f.emails, ''])} className="text-xs text-[#0073ea]">+ Add email</button>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs text-gray-500">Call status</Label>
            <Select value={f.call_status} onValueChange={v => up('call_status', v)}><SelectTrigger data-testid="contact-call-status" className={cls}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{CALL_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1"><Label className="text-xs text-gray-500">SMS consent source</Label><Input value={f.sms_opt_in_source} onChange={e => up('sms_opt_in_source', e.target.value)} placeholder="e.g. Verbal on call 6/2" className={cls} /></div>
        </div>
        <div className="flex flex-wrap gap-4 text-sm text-gray-700">
          <label className="flex items-center gap-2"><Switch data-testid="contact-sms-optin" checked={f.sms_opt_in} onCheckedChange={v => up('sms_opt_in', v)} /> SMS opt-in</label>
          <label className="flex items-center gap-2"><Switch checked={f.email_opt_out} onCheckedChange={v => up('email_opt_out', v)} /> Email opt-out</label>
          <label className="flex items-center gap-2"><Switch data-testid="contact-litigator" checked={f.litigator} onCheckedChange={v => up('litigator', v)} /> Litigator</label>
        </div>
        <Button data-testid="contact-save-btn" onClick={save} className="bg-[#0073ea] hover:bg-[#0060c2] text-white">Save Contact</Button>
      </DialogContent>
    </Dialog>
  );
}

function ContactRow({ c, onEdit, onDelete, onStatus }) {
  return (
    <div data-testid={`contact-${c.id}`} className="p-3 rounded-lg border border-gray-200 space-y-2">
      <div className="flex items-center gap-2">
        <p className="font-semibold text-sm text-gray-900 flex-1">{c.first_name} {c.last_name} {c.title && <span className="text-gray-400 font-normal">· {c.title}</span>}</p>
        {c.litigator && <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200"><ShieldAlert className="h-3 w-3 mr-0.5" />Litigator</Badge>}
        {c.sms_opt_in && <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">SMS opt-in</Badge>}
        {c.email_opt_out && <Badge variant="outline" className="text-[10px]">Email opt-out</Badge>}
        <button onClick={() => onEdit(c)} className="p-2 -m-1 sm:p-0 sm:m-0 text-gray-300 hover:text-gray-600" data-testid={`edit-contact-${c.id}`}><Pencil className="h-3.5 w-3.5" /></button>
        <button onClick={() => onDelete(c)} className="p-2 -m-1 sm:p-0 sm:m-0 text-gray-300 hover:text-red-500" data-testid={`delete-contact-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
        {c.phones.map(p => (
          <a key={p.number} href={p.dnc ? undefined : `tel:${p.number}`} className={`flex items-center gap-1 ${p.dnc ? 'text-gray-400 line-through' : 'hover:text-[#0073ea]'}`}>
            <Phone className="h-3 w-3" />{fmtPhone(p.number)} <span className="text-[10px] text-gray-400">{p.type}{p.dnc ? ' · DNC' : ''}</span>
          </a>
        ))}
        {c.emails.map(e => <a key={e} href={`mailto:${e}`} className="flex items-center gap-1 hover:text-[#0073ea]"><Mail className="h-3 w-3" />{e}</a>)}
      </div>
      <Select value={c.call_status} onValueChange={v => onStatus(c, v)}>
        <SelectTrigger data-testid={`call-status-${c.id}`} className="h-7 w-48 text-xs bg-white"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white">{CALL_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

export function ContactsCard({ buyerId, onChanged }) {
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState(null);
  const load = () => API.get(`/buyers/${buyerId}/contacts`).then(r => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, [buyerId]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = () => { load(); onChanged(); };
  const del = async (c) => { if (!window.confirm('Delete contact?')) return; await API.delete(`/buyers/${buyerId}/contacts/${c.id}`); refresh(); };
  const status = async (c, v) => { await API.put(`/buyers/${buyerId}/contacts/${c.id}`, { call_status: v }); toast.success('Call status updated'); refresh(); };
  return (
    <Card className="bg-white border-gray-200 shadow-sm" data-testid="contacts-card">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-base font-semibold text-gray-900 flex items-center gap-2"><Users className="h-4 w-4 text-[#0073ea]" /> Contacts <span className="text-gray-400 font-normal text-sm">{items.length}</span></p>
          <Button size="sm" variant="outline" data-testid="add-contact-btn" onClick={() => setDialog({})}><Plus className="h-3.5 w-3.5 mr-1" /> Add Contact</Button>
        </div>
        {items.length === 0 ? <p className="text-sm text-gray-400 text-center py-3">No contacts yet</p> : (
          <div className="grid md:grid-cols-2 gap-2">{items.map(c => <ContactRow key={c.id} c={c} onEdit={x => setDialog({ contact: x })} onDelete={del} onStatus={status} />)}</div>
        )}
      </CardContent>
      <ContactDialog open={!!dialog} onOpenChange={v => !v && setDialog(null)} contact={dialog?.contact} buyerId={buyerId} onSaved={refresh} />
    </Card>
  );
}
